import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiPublic } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowRight, Check, Tag, Sparkles, ArrowLeft, X,
  WashingMachine, Refrigerator, Microwave, Wind, Flame, ChefHat,
  Snowflake, Trash2, Wrench, Droplets,
} from "lucide-react";

const APPLIANCES = [
  { key: "washer",       icon: WashingMachine, label: "Washer" },
  { key: "dryer",        icon: Wind,           label: "Dryer" },
  { key: "refrigerator", icon: Refrigerator,   label: "Fridge" },
  { key: "dishwasher",   icon: Droplets,       label: "Dishwasher" },
  { key: "oven",         icon: ChefHat,        label: "Oven" },
  { key: "stove",        icon: Flame,          label: "Stove" },
  { key: "microwave",    icon: Microwave,      label: "Microwave" },
  { key: "ice-maker",    icon: Snowflake,      label: "Ice maker" },
  { key: "disposal",     icon: Trash2,         label: "Disposal" },
  { key: "other",        icon: Wrench,         label: "Other" },
];

// Two-hour service windows — labels match the dispatcher new-job picker.
const TIME_BLOCKS = [
  { startHour: 8,  label: "Morning",   range: "8:00 AM – 10:00 AM" },
  { startHour: 10, label: "Midday",    range: "10:00 AM – 12:00 PM" },
  { startHour: 12, label: "Lunchtime", range: "12:00 PM – 2:00 PM" },
  { startHour: 14, label: "Afternoon", range: "2:00 PM – 4:00 PM" },
  { startHour: 16, label: "Late day",  range: "4:00 PM – 6:00 PM" },
  { startHour: 18, label: "Evening",   range: "6:00 PM – 8:00 PM" },
];

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Shared 4-step booking wizard — mobile-first tile + date-strip UX.
 *   1. Appliance (tile grid)
 *   2. When (date strip + 2-hour blocks)
 *   3. Where & you (contact + address; autocomplete-friendly)
 *   4. Confirm (sticky submit + promo)
 *
 * Variants:
 *   - "page"  : standalone booking page (Book.jsx) — neutral cream/light wrap
 *   - "embed" : embedded on retro landing (Marketing.jsx) — navy/cream/orange retro
 */
export default function BookingWizard({ variant = "page" }) {
  const nav = useNavigate();
  const isEmbed = variant === "embed";
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", address: "",
    appliance: "", issue: "",
    scheduled_for: "",   // ISO output from picker
    scheduled_label: "", // human-readable label for review/submit
    promo_code: "GRAND_OPENING",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canNext1 = form.appliance !== "";
  const canNext2 = !!form.scheduled_for;
  const canSubmit = canNext1 && canNext2 && form.name && form.phone && form.address;

  async function submit() {
    setSubmitting(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const payload = {
        ...form,
        // Keep legacy preferred_window populated for the staff CRM display
        preferred_window: form.scheduled_label || form.scheduled_for,
        promo_code: form.promo_code?.trim() ? form.promo_code.trim().toUpperCase() : undefined,
        utm_source: params.get("utm_source") || undefined,
        utm_medium: params.get("utm_medium") || undefined,
        utm_campaign: params.get("utm_campaign") || undefined,
        landing_url: window.location.href,
        referrer: document.referrer || undefined,
      };
      const res = await apiPublic.post("/leads", payload);
      const promoApplied = res.data?.promo_applied;
      toast.success(promoApplied ? "Got it — your $10 launch credit is locked in." : "Booked! A dispatcher will confirm shortly.");
      setResult(res.data);
      setStep(5);
      setTimeout(() => nav(`/track?phone=${encodeURIComponent(form.phone)}`), 2400);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // Theme tokens — drives the navy-on-cream retro look on the marketing page
  // and a clean white surface for the standalone /book page.
  const t = isEmbed
    ? {
        wrap: "bg-[#FBF6E8] text-[#0B192C] rounded-2xl border-4 border-[#E65C00] shadow-[0_0_80px_rgba(230,92,0,0.35)] overflow-hidden",
        title: "display text-[#0B192C] text-2xl sm:text-3xl font-semibold tracking-tight",
        sub: "text-[#0B192C]/60 text-sm",
        progressActive: "bg-[#E65C00]",
        progressIdle: "bg-[#0B192C]/15",
        tileIdle: "border-[#0B192C]/15 bg-[#FFFFFF] text-[#0B192C] hover:border-[#E65C00] hover:bg-[#E65C00]/5",
        tileActive: "border-[#E65C00] bg-[#E65C00]/10 text-[#E65C00] shadow-sm",
        primary: "bg-[#E65C00] hover:bg-[#ff7a1f] text-white rounded-full h-12 px-6 font-bold uppercase tracking-widest text-sm shadow-md",
        secondary: "border border-[#0B192C]/25 text-[#0B192C] hover:bg-[#0B192C]/5 rounded-full h-12 px-5",
        input: "bg-white border-[#0B192C]/20 text-[#0B192C] placeholder:text-[#0B192C]/40 focus-visible:ring-[#E65C00] focus-visible:ring-2 focus-visible:border-[#E65C00] h-12",
        label: "text-[10px] uppercase tracking-[0.2em] text-[#0B192C]/60 font-bold",
      }
    : {
        wrap: "bg-white text-slate-900 rounded-2xl border border-slate-200 shadow-xl overflow-hidden",
        title: "display text-slate-900 text-2xl sm:text-3xl font-semibold tracking-tight",
        sub: "text-slate-500 text-sm",
        progressActive: "bg-[#E65C00]",
        progressIdle: "bg-slate-200",
        tileIdle: "border-slate-200 bg-white text-slate-700 hover:border-[#E65C00] hover:bg-[#E65C00]/5",
        tileActive: "border-[#E65C00] bg-[#E65C00]/10 text-[#E65C00] shadow-sm",
        primary: "bg-[#E65C00] hover:bg-[#ff7a1f] text-white rounded-full h-12 px-6 font-bold uppercase tracking-widest text-sm shadow-md",
        secondary: "border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-full h-12 px-5",
        input: "bg-white border-slate-200 focus-visible:ring-[#E65C00] focus-visible:ring-2 focus-visible:border-[#E65C00] h-12",
        label: "text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold",
      };

  return (
    <div className={t.wrap} data-testid="booking-wizard">
      {/* Top progress bar — high-contrast, advances by step */}
      <div className="flex h-1.5">
        {[1, 2, 3, 4].map((s) => (
          <div
            key={s}
            className={`flex-1 transition-colors ${step >= s ? t.progressActive : t.progressIdle}`}
          />
        ))}
      </div>

      {/* Step label (replaces the bullet text in the spec) */}
      <div className="px-6 sm:px-8 pt-5 pb-2 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#E65C00]">
          {step <= 4 ? `Step ${step} of 4` : "All set"}
        </span>
        {step > 1 && step < 5 && (
          <button
            type="button"
            data-testid="book-back-floating"
            onClick={() => setStep((s) => s - 1)}
            className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-70 hover:opacity-100 hover:text-[#E65C00] flex items-center gap-1"
          >
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
        )}
      </div>

      <div className="px-5 sm:px-8 pb-5">
        {step === 1 && <StepAppliance t={t} value={form.appliance} onPick={(v) => update("appliance", v)} issue={form.issue} onIssue={(v) => update("issue", v)} onNext={() => setStep(2)} canNext={canNext1} />}
        {step === 2 && <StepWhen t={t} value={form.scheduled_for} onPick={(iso, lbl) => { update("scheduled_for", iso); update("scheduled_label", lbl); }} onNext={() => setStep(3)} canNext={canNext2} />}
        {step === 3 && <StepWho t={t} form={form} update={update} onNext={() => setStep(4)} canNext={canSubmit} />}
        {step === 4 && <StepConfirm t={t} form={form} update={update} submitting={submitting} onSubmit={submit} />}
        {step === 5 && (
          <div className="text-center pt-6 pb-4">
            <div className={`w-16 h-16 rounded-full grid place-items-center mx-auto bg-[#E65C00]/15 text-[#E65C00]`}>
              <Check className="w-8 h-8" />
            </div>
            <h2 className={`${t.title} mt-5`}>You&apos;re in the queue.</h2>
            <p className={`${t.sub} mt-1.5`}>
              {result?.promo_applied ? "$10 launch credit locked in. " : ""}
              Redirecting you to your tracker…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Step 1 — Appliance tile grid + optional issue note
// =============================================================================
function StepAppliance({ t, value, onPick, issue, onIssue, onNext, canNext }) {
  return (
    <div className="fade-up">
      <h2 className={t.title}>What needs fixing?</h2>
      <p className={`${t.sub} mt-1`}>Tap your appliance — we&apos;ll handle the rest.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mt-5" data-testid="book-appliance-grid">
        {APPLIANCES.map((a) => {
          const Icon = a.icon;
          const selected = value === a.key;
          return (
            <button
              key={a.key}
              type="button"
              data-testid={`book-appliance-${a.key}`}
              onClick={() => onPick(a.key)}
              className={`flex flex-col items-center justify-center gap-1.5 py-4 px-2 rounded-xl border-2 transition-all min-h-[88px] ${selected ? t.tileActive : t.tileIdle}`}
            >
              <Icon className={`w-7 h-7 ${selected ? "stroke-[2.5]" : ""}`} />
              <span className="text-xs font-semibold uppercase tracking-wide">{a.label}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        <label className={t.label}>What&apos;s happening? (optional)</label>
        <Textarea
          data-testid="book-issue"
          className={`mt-1.5 ${t.input}`}
          value={issue}
          onChange={(e) => onIssue(e.target.value)}
          placeholder="e.g. fridge not cooling, water on the floor"
          rows={2}
          style={{ minHeight: 64 }}
        />
      </div>

      <div className="mt-6 flex justify-end">
        <Button data-testid="book-next-1" disabled={!canNext} onClick={onNext} className={t.primary}>
          Next <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// Step 2 — Date strip + 2-hour blocks
// =============================================================================
function StepWhen({ t, value, onPick, onNext, canNext }) {
  const today = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  }, []);
  const days = useMemo(() => Array.from({ length: 14 }, (_, i) => {
    const d = new Date(today); d.setDate(d.getDate() + i); return d;
  }), [today]);

  const parsed = useMemo(() => {
    if (!value) return { dateKey: null, hour: null };
    const d = new Date(value); if (Number.isNaN(d.getTime())) return { dateKey: null, hour: null };
    return { dateKey: `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`, hour: d.getHours() };
  }, [value]);

  const [activeDate, setActiveDate] = useState(() => parsed.dateKey ? new Date(value) : null);
  const activeDateKey = activeDate
    ? `${activeDate.getFullYear()}-${activeDate.getMonth()}-${activeDate.getDate()}`
    : null;

  function emit(dt, hour) {
    const out = new Date(dt); out.setHours(hour, 0, 0, 0);
    const block = TIME_BLOCKS.find((b) => b.startHour === hour);
    const label = `${WEEKDAY[out.getDay()]} ${MONTH[out.getMonth()]} ${out.getDate()} · ${block ? block.range : ""}`;
    onPick(out.toISOString(), label);
  }
  function pickDate(d) {
    setActiveDate(d);
    if (parsed.hour != null) emit(d, parsed.hour);
  }
  function pickBlock(hour) {
    const base = activeDate || today;
    if (!activeDate) setActiveDate(base);
    emit(base, hour);
  }

  return (
    <div className="fade-up">
      <h2 className={t.title}>When works best?</h2>
      <p className={`${t.sub} mt-1`}>Pick a day, then a 2-hour window.</p>

      {/* Horizontal date strip — scrollable */}
      <div
        className="flex gap-2 overflow-x-auto pb-2 pt-3 -mx-5 sm:-mx-8 px-5 sm:px-8 snap-x snap-mandatory"
        data-testid="book-date-strip"
        style={{ scrollbarWidth: "thin" }}
      >
        {days.map((d, idx) => {
          const dk = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
          const isToday = dk === `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
          const isEarliest = idx === 0; // "Earliest" tag on today
          const selected = dk === activeDateKey;
          return (
            <button
              key={dk}
              type="button"
              data-testid={`book-date-${dk}`}
              onClick={() => pickDate(d)}
              className={`shrink-0 snap-start relative w-[68px] h-[88px] rounded-xl border-2 flex flex-col items-center justify-center transition-all ${
                selected ? t.tileActive : t.tileIdle
              }`}
            >
              <span className="text-[10px] uppercase tracking-widest font-bold opacity-70">{WEEKDAY[d.getDay()]}</span>
              <span className="text-2xl font-bold leading-none mt-1 num">{d.getDate()}</span>
              <span className="text-[9px] uppercase tracking-widest font-bold mt-1 opacity-60">{MONTH[d.getMonth()]}</span>
              {(isToday || isEarliest) && (
                <span className="absolute -top-1.5 -right-1.5 bg-[#E65C00] text-white text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full shadow-sm">
                  ★ Earliest
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Vertical 2-hour blocks */}
      <div className="mt-4 space-y-2" data-testid="book-time-list">
        {TIME_BLOCKS.map((b) => {
          const selected = activeDateKey && parsed.hour === b.startHour;
          // Naive availability cue — last block of the day shows "Filling up"
          const filling = b.startHour === 18;
          return (
            <button
              key={b.startHour}
              type="button"
              data-testid={`book-slot-${b.startHour}`}
              onClick={() => pickBlock(b.startHour)}
              disabled={!activeDate}
              className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all flex items-center justify-between gap-3 ${
                selected
                  ? t.tileActive
                  : !activeDate
                    ? "border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed"
                    : t.tileIdle
              }`}
            >
              <div>
                <div className="font-bold text-base">{b.label}</div>
                <div className="text-xs num opacity-70 mt-0.5">{b.range}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${
                selected
                  ? "bg-[#E65C00] text-white"
                  : filling
                    ? "border border-amber-400 text-amber-600 bg-amber-50"
                    : "border border-emerald-400 text-emerald-600 bg-emerald-50"
              }`}>
                {selected ? "Picked" : filling ? "Filling" : "Open"}
              </span>
            </button>
          );
        })}
        {!activeDate && (
          <div className="text-xs text-slate-400 italic text-center pt-1">Pick a day above to see times.</div>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <Button data-testid="book-next-2" disabled={!canNext} onClick={onNext} className={t.primary}>
          Next <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// Step 3 — Who & where (autocomplete-aggressive, mobile keyboard tuned)
// =============================================================================
function StepWho({ t, form, update, onNext, canNext }) {
  return (
    <div className="fade-up">
      <h2 className={t.title}>You & the address</h2>
      <p className={`${t.sub} mt-1`}>Most browsers will autofill — tap a field to use saved contact info.</p>
      <div className="grid sm:grid-cols-2 gap-3 mt-5">
        <Field t={t} label="Your name *">
          <Input
            data-testid="book-name"
            className={t.input}
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Jane Doe"
            autoComplete="name"
            enterKeyHint="next"
          />
        </Field>
        <Field t={t} label="Phone *">
          <Input
            data-testid="book-phone"
            className={`${t.input} num`}
            type="tel"
            inputMode="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="(210) 555-0199"
            autoComplete="tel"
            enterKeyHint="next"
          />
        </Field>
        <Field t={t} label="Email (for confirmation)" full>
          <Input
            data-testid="book-email"
            className={t.input}
            type="email"
            inputMode="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="jane@example.com"
            autoComplete="email"
            enterKeyHint="next"
          />
        </Field>
        <Field t={t} label="Service address *" full>
          <Input
            data-testid="book-address"
            className={t.input}
            value={form.address}
            onChange={(e) => update("address", e.target.value)}
            placeholder="123 Main St, San Antonio, TX"
            autoComplete="street-address"
            enterKeyHint="done"
          />
        </Field>
      </div>
      <div className="mt-6 flex justify-end">
        <Button data-testid="book-next-3" disabled={!canNext} onClick={onNext} className={t.primary}>
          Review <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// Step 4 — Confirm + sticky CTA
// =============================================================================
function StepConfirm({ t, form, update, submitting, onSubmit }) {
  return (
    <div className="fade-up">
      <h2 className={t.title}>One last look</h2>
      <p className={`${t.sub} mt-1`}>If anything&apos;s off, tap Back below.</p>

      <div className="mt-5 rounded-xl border border-[#E65C00]/20 bg-[#FFF7E6] divide-y divide-[#E65C00]/10">
        <Row k="Appliance"   v={form.appliance.replace("-", " ")} icon="🔧" />
        <Row k="Issue"       v={form.issue || "—"} />
        <Row k="When"        v={form.scheduled_label || "—"} />
        <Row k="Name"        v={form.name} />
        <Row k="Phone"       v={form.phone} numeric />
        <Row k="Email"       v={form.email || "—"} />
        <Row k="Address"     v={form.address} />
      </div>

      <div className="mt-4">
        <label className={t.label}>
          <Tag className="w-3 h-3 inline mr-1 -mt-0.5" /> Promo code
        </label>
        <div className="mt-1.5 relative">
          <Input
            data-testid="book-promo"
            className={t.input}
            value={form.promo_code}
            onChange={(e) => update("promo_code", e.target.value.toUpperCase())}
            placeholder="GRAND_OPENING"
          />
          {form.promo_code && (
            <button
              type="button"
              onClick={() => update("promo_code", "")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#E65C00]"
              aria-label="Clear promo"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {form.promo_code?.toUpperCase() === "GRAND_OPENING" && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-[#E65C00] font-semibold bg-[#E65C00]/10 border border-[#E65C00]/30 rounded-md p-2">
            <Sparkles className="w-3.5 h-3.5" /> $10 launch credit applied to your repair.
          </div>
        )}
      </div>

      {/* Sticky-feeling confirm CTA — full width on mobile, prominent on desktop */}
      <div className="mt-6 sticky bottom-0 bg-gradient-to-t from-white via-white to-transparent pt-4 -mx-5 sm:-mx-8 px-5 sm:px-8 pb-1">
        <Button
          data-testid="book-submit"
          disabled={submitting}
          onClick={onSubmit}
          className="w-full h-14 bg-[#E65C00] hover:bg-[#ff7a1f] text-white rounded-full font-bold uppercase tracking-widest text-sm shadow-lg flex items-center justify-center gap-2"
        >
          {submitting ? "Sending…" : (
            <>
              <Check className="w-5 h-5" /> Confirm appointment
            </>
          )}
        </Button>
        <p className="text-center text-[10px] uppercase tracking-widest text-slate-400 mt-2 pb-2">
          No payment now — a dispatcher will confirm by text in ~30 min.
        </p>
      </div>
    </div>
  );
}

function Field({ label, full, children, t }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className={t.label}>{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Row({ k, v, numeric }) {
  return (
    <div className="flex justify-between gap-4 px-4 py-2.5 text-sm">
      <span className="text-slate-500 uppercase tracking-wider text-[11px] font-semibold mt-0.5">{k}</span>
      <span className={`text-slate-900 font-semibold text-right break-words capitalize ${numeric ? "num normal-case" : ""}`}>{v}</span>
    </div>
  );
}
